import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

// Icons
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export const RegistrarUsuarioDNI = () => {
  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast();

  const [managers, setManagers] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [nuevoUsuario, setNuevoUsuario] = useState({
    nombre: "",
    apellidos: "",
    dni: "",
    email: "",
    password: "",
    rol: "usuario",
    gerente_id: "",
    telefono: "",
  });

  const [confirmPassword, setConfirmPassword] = useState("");

  // Avatar (opcional)
  const [avatarFile, setAvatarFile] = useState(null);

  // =========================
  // Validación passwords
  // =========================
  const pwdMismatch = useMemo(() => {
    const p1 = nuevoUsuario.password || "";
    const p2 = confirmPassword || "";
    if (!p1 && !p2) return false;
    return p1 !== p2;
  }, [nuevoUsuario.password, confirmPassword]);

  // =========================
  // Carga managers
  // =========================
  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${Global.url}usuarios`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setManagers((Array.isArray(data) ? data : []).filter((u) => u.rol === "gerente"));
        } else {
          setManagers([]);
        }
      } catch (err) {
        console.error("Error al obtener managers:", err);
        setManagers([]);
      }
    })();
  }, []);

  // =========================
  // Opciones manager (Autocomplete)
  // =========================
  const managersOrdenados = useMemo(() => {
    return (managers || [])
      .slice()
      .sort((a, b) =>
        `${a.nombre} ${a.apellidos || ""}`.localeCompare(`${b.nombre} ${b.apellidos || ""}`)
      );
  }, [managers]);

  const managerSeleccionado =
    managersOrdenados.find((m) => String(m.id) === String(nuevoUsuario.gerente_id)) || null;

  // =========================
  // Helpers
  // =========================
  const setField = (k, v) => setNuevoUsuario((p) => ({ ...p, [k]: v }));

  const onAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAvatarFile(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setMensaje("❌ El archivo seleccionado no es una imagen.");
      setAvatarFile(null);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setMensaje("❌ La imagen no puede superar los 3MB.");
      setAvatarFile(null);
      return;
    }
    setMensaje("");
    setAvatarFile(file);
  };

  // =========================
  // Submit
  // =========================
  const registrarUsuario = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!nuevoUsuario.password) {
      setMensaje("❌ La contraseña es obligatoria.");
      return;
    }
    if ((nuevoUsuario.password || "").length < 8) {
      setMensaje("❌ La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (pwdMismatch) {
      setMensaje("❌ Las contraseñas no coinciden.");
      return;
    }

    setIsSubmitting(true);
    setMensaje("");

    const payload = {
      ...nuevoUsuario,
      dni: (nuevoUsuario.dni || "").toUpperCase().replace(/\s|-/g, ""),
      email: (nuevoUsuario.email || "").trim().toLowerCase() || null,
      gerente_id: nuevoUsuario.gerente_id || null,
      telefono: (nuevoUsuario.telefono || "").trim() || null,
    };

    try {
      const response = await fetch(`${Global.url}usuarios/admin/register-dni`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMensaje(`❌ ${data?.message || "Error al registrar el usuario."}`);
        return;
      }

      const createdUser = data?.user;
      toast.success("Usuario registrado correctamente.");

      // Subida de avatar (si hay)
      if (createdUser?.id && avatarFile) {
        try {
          const fd = new FormData();
          fd.append("avatar", avatarFile);

          const avatarRes = await fetch(`${Global.url}usuarios/${createdUser.id}/avatar`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: fd,
          });

          if (!avatarRes.ok) {
            const avatarData = await avatarRes.json().catch(() => ({}));
            console.error("Error al subir avatar:", avatarData);
            toast.warning("Usuario creado, pero el avatar no se pudo subir.");
          } else {
            toast.success("Avatar subido correctamente.");
          }
        } catch (err) {
          console.error("Error de red al subir avatar:", err);
          toast.warning("Usuario creado, pero hubo un error subiendo el avatar.");
        }
      }

      // Reset
      setNuevoUsuario({
        nombre: "",
        apellidos: "",
        dni: "",
        email: "",
        password: "",
        rol: "usuario",
        gerente_id: "",
        telefono: "",
      });
      setConfirmPassword("");
      setAvatarFile(null);

      setTimeout(() => setMensaje(""), 2000);
    } catch (error) {
      console.error("Error al registrar usuario:", error);
      setMensaje("❌ Error de conexión con el servidor.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Registrar nuevo usuario (DNI)
        </Typography>

        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Paper sx={{ p: 2, width: "100%", maxWidth: 900 }}>
            <Box
              component="form"
              onSubmit={registrarUsuario}
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                alignItems: "end", // 🔑 alinear por "suelo"
              }}
            >
              {/* DNI + Email */}
              <TextField
                label="DNI (obligatorio)"
                value={nuevoUsuario.dni}
                onChange={(e) => setField("dni", e.target.value)}
                required
                placeholder="12345678Z"
                autoComplete="off"
                size="small"
              />
              <TextField
                label="Email (opcional)"
                value={nuevoUsuario.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="usuario@dominio.com"
                autoComplete="off"
                size="small"
              />

              {/* Nombre + Apellidos */}
              <TextField
                label="Nombre"
                value={nuevoUsuario.nombre}
                onChange={(e) => setField("nombre", e.target.value)}
                required
                autoComplete="off"
                size="small"
              />
              <TextField
                label="Apellidos"
                value={nuevoUsuario.apellidos}
                onChange={(e) => setField("apellidos", e.target.value)}
                required
                autoComplete="off"
                size="small"
              />

              {/* Teléfono + Avatar (misma fila, misma altura) */}
              <TextField
                label="Teléfono (opcional)"
                value={nuevoUsuario.telefono}
                onChange={(e) => setField("telefono", e.target.value)}
                placeholder="Ej: 600 123 456"
                autoComplete="off"
                size="small"
              />

              <TextField
                label="Foto / Avatar (opcional)"
                value={
                  avatarFile
                    ? `${avatarFile.name} (${Math.round(avatarFile.size / 1024)} KB)`
                    : ""
                }
                placeholder="Sin archivo"
                size="small"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton component="label" edge="end" size="small">
                        <AttachFileIcon fontSize="small" />
                        <input hidden type="file" accept="image/*" onChange={onAvatarChange} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password + Confirm (misma altura, con toggle) */}
              <TextField
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                value={nuevoUsuario.password}
                onChange={(e) => setField("password", e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                        title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirmar contraseña"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                size="small"
                error={pwdMismatch}
                helperText={pwdMismatch ? "Las contraseñas no coinciden." : " "}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                        title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Rol + Manager */}
              <FormControl fullWidth size="small">
                <InputLabel id="rol-label">Rol</InputLabel>
                <Select
                  labelId="rol-label"
                  label="Rol"
                  value={nuevoUsuario.rol}
                  onChange={(e) => setField("rol", e.target.value)}
                  required
                >
                  <MenuItem value="usuario">Usuario</MenuItem>
                  <MenuItem value="gerente">Gerente</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                </Select>
              </FormControl>

              <Autocomplete
                size="small"
                options={managersOrdenados}
                getOptionLabel={(u) => `${u.nombre} ${u.apellidos || ""}`.trim()}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={managerSeleccionado}
                onChange={(_, newValue) => setField("gerente_id", newValue ? String(newValue.id) : "")}
                renderInput={(params) => (
                  <TextField {...params} label="Manager (opcional)" placeholder="Sin manager" size="small" />
                )}
                clearOnEscape
              />

              {/* Mensaje (fila completa) */}
              {mensaje && (
                <Typography
                  sx={{ gridColumn: "1 / -1" }}
                  color={mensaje.includes("❌") ? "error" : "success.main"}
                >
                  {mensaje}
                </Typography>
              )}

              {/* Botón (fila completa) */}
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  justifyContent: "flex-end",
                  pt: 1,
                }}
              >
                <Button
                  type="submit"
                  disabled={isSubmitting || pwdMismatch}
                  sx={{ height: 40, minWidth: 180 }}
                  title={pwdMismatch ? "Las contraseñas no coinciden" : undefined}
                >
                  {isSubmitting ? "Registrando..." : "Registrar"}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
